import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import { RecommendationService } from "JS/Services/Recommendation";
import { Content, RecommendationBatch, Recommendations } from "JS/Models";
import {
    RecommendationsState,
    setGlobalRecommendations,
} from "JS/Redux/Recommendations";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useRouting } from "./Routes";
import { AudioCategoryTitle } from "../Scenes/Audios";
import {
    BlackRecommendationsState,
    setGlobalBlackRecommendations,
} from "JS/Redux/BlackRecommendations";
import { useVerifyAwsKeys } from "./Media";
import { checkIfBatchAllowsNotify, checkIfBatchIncludesIbos } from "JS/Helpers";
import { uniqBy } from "lodash-es";
import { captureSentryError } from "JS/Helpers/SentryHelper";
import { getIsLtdConsentFormOpened } from "JS/Helpers/LocalStorageHelpers";

export const rService = new RecommendationService();

export const useRecommendations = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);

    const { setGlobalRecommendations, recommendationState } =
        useGlobalRecommendations();

    const { enqueueSnackbar } = useSnackbar();

    const refetch = useCallback(() => {
        setLoading(true);

        return rService
            .getRecommendedAudios()
            .then((response) => {
                if (!response?.status) {
                    enqueueSnackbar((response as any)?.message, {
                        variant: "error",
                    });
                } else {
                    const apiRecommendations = {
                        recommendations: response ? response.data : [],
                    };
                    setGlobalRecommendations(apiRecommendations);

                    return apiRecommendations;
                }
                return {
                    recommendations: [],
                };
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });
                return {
                    recommendations: [],
                };
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) refetch();
    }, [skip]);

    return {
        refetch,
        recommendations: recommendationState?.recommendations,
        loading,
    };
};

export const useRecommendationsBatch = () => {
    const [loading, setLoading] = useState<boolean>(false);

    const { recommendationState, setGlobalRecommendations } =
        useGlobalRecommendations();
    const { verifyAwsKeys } = useVerifyAwsKeys();

    const refetch = useCallback(async () => {
        setLoading(true);
        await verifyAwsKeys();
        return rService
            .getS3AudioRecommendationBatch()
            .then((batch) => {
                setGlobalRecommendations({
                    batch: batch,
                    lastFetched: moment().unix(),
                });
                return batch;
            })
            .catch((err) => {
                if (
                    err.code &&
                    typeof err.code === "string" &&
                    err.code !== "NetworkingError" &&
                    !err.code.includes("permission")
                )
                    captureSentryError(err, {
                        location: "recommendations-batch",
                    });
                return {
                    batchId: null,
                    ibos: [],
                } as RecommendationBatch;
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    return {
        batch: recommendationState.batch,
        loading,
        refetch,
    };
};

export const useFilteredRecommendations = (
    recommendations: Recommendations[] = [],
    audios: Content[] = [],
) => {
    const filteredRecommendations = useMemo(() => {
        const recommendedNids = recommendations?.map((d) => `${d.nid}`);
        const audiosNids = audios?.map((d) => d.nid);
        return {
            filteredRecommendedAudios: uniqBy(
                audios?.filter((a) => recommendedNids?.includes(`${a.nid}`)),
                (x) => x.nid,
            ),
            filteredRecommendations: uniqBy(
                recommendations?.filter((r) =>
                    audiosNids?.includes(`${r.nid}`),
                ),
                (x) => x.nid,
            ),
        };
    }, [recommendations, audios]);

    return filteredRecommendations;
};

export const useGlobalRecommendations = () => {
    const dispatch = useAppDispatch();

    const allData = useAppSelector((state) => state.recommendations);

    const setRecommendations = (state: Partial<RecommendationsState>) => {
        dispatch(setGlobalRecommendations(state));
    };

    return {
        recommendationState: allData,
        setGlobalRecommendations: setRecommendations,
    };
};

export const useGlobalBlackRecommendations = () => {
    const dispatch = useAppDispatch();

    const allData = useAppSelector((state) => state.blackRecommendations);
    const setBlackRecommendations = (
        state: Partial<BlackRecommendationsState>,
    ) => {
        dispatch(setGlobalBlackRecommendations(state));
    };

    return {
        blackRecommendationState: allData,
        setGlobalBlackRecommendations: setBlackRecommendations,
    };
};

export const useOpenAutoPopup = () => {
    const {
        recommendationState: { noOfPopups, recommendations, batch },
    } = useGlobalRecommendations();

    const { showRedirectionDialog, loadingConsent } = useAppSelector(
        (state) => state.consentForm,
    );

    const history = useHistory();
    const { linkProvider } = useRouting();

    const onlyOnce =
        recommendations?.length === 0 ? noOfPopups < 1 : noOfPopups < 3;

    const includeIbos = batch && checkIfBatchIncludesIbos(batch);
    const allowsNotify = batch && checkIfBatchAllowsNotify(batch);

    const allowOpen = onlyOnce && includeIbos && allowsNotify;

    const openPopup = (
        categoryId: string = undefined,
        must: boolean = false,
    ) => {
        if (
            !showRedirectionDialog &&
            !loadingConsent &&
            getIsLtdConsentFormOpened() !== "true" &&
            ((allowOpen && categoryId !== AudioCategoryTitle.RECOMMENDED) ||
                must)
        )
            history.push(linkProvider.react.recommendations().index(true));
    };

    return {
        openPopup,
    };
};
